import React from "react";
import { Link } from "react-router-dom";
import { useApp } from "../context/AppContext";
import { useI18n } from "../i18n";
import { FEATURE_MODULE_MAP } from "../config/feature-modules";
import { startingPrice } from "../config/plans";
import { MODULE_ADDON_PRICES } from "../config/addons";
import { Badge, Button, Card, baht } from "./ui";
import Icon from "./ui/Icon";

// ---------------------------------------------------------------------------
// ด่านตรวจแพ็กเกจ
// ---------------------------------------------------------------------------
// ลำดับการตรวจของทุกหน้าฝั่ง /admin
//   1. ยังไม่ซื้อ / หมดอายุ      -> ใช้ชุดฟรี (FREE_TIER) เข้าได้ทุกหน้า
//   2. โมดูลไม่อยู่ในแพ็กเกจ     -> หน้าล็อก พร้อมบอกว่าต้องซื้อ/อัปเกรดเป็นแพ็กเกจไหน
//   3. โมดูลถูกปิดที่ Module Manager -> หน้าแจ้งให้ไปเปิด
// ---------------------------------------------------------------------------

/** หน้าจอเมื่อโมดูลถูกล็อกเพราะแพ็กเกจไม่ครอบคลุม */
export function LockedFeature({ module: moduleKey }) {
  const { upgradeFor, plan, planFree } = useApp();
  const { tx, pick } = useI18n();
  const feature = FEATURE_MODULE_MAP[moduleKey];
  const needed = upgradeFor(moduleKey);

  return (
    <Card className="u-center" style={{ padding: 32 }}>
      <div style={{ fontSize: 44, color: "var(--c-indigo-600)" }}><Icon name="lock" /></div>
      <h2 style={{ fontSize: 22, fontWeight: 800, margin: "10px 0 4px" }}>
        {!planFree
          ? tx("ฟีเจอร์นี้ยังไม่ได้เปิดในแพ็กเกจของคุณ", "This feature is not in your plan")
          : tx("ต้องซื้อแพ็กเกจเพื่อใช้งานเมนูนี้", "Buy a plan to use this menu")}
      </h2>
      <div className="u-muted u-fs-13">
        {feature ? pick(feature) : moduleKey} — {feature?.detail || ""}
      </div>

      {needed ? (
        <div className="card-crm u-mt-16" style={{ maxWidth: 420, margin: "16px auto 0" }}>
          <div className="u-fs-13 u-muted">{tx("ปลดล็อกด้วย", "Unlock with")}</div>
          <div className="u-strong" style={{ fontSize: 20 }}>{pick(needed)}</div>
          <div className="u-fs-13 u-muted">
            {needed.quoteOnly
              ? tx("ราคาตามใบเสนอราคา", "Custom quote")
              : `${tx("เริ่มต้น", "From")} ${baht(startingPrice(needed.key), 0)} / ${tx("เดือน", "month")}`}
          </div>
          <div className="u-fs-12 u-muted u-mt-8">
            {tx("แพ็กเกจปัจจุบัน", "Current plan")}: {plan ? pick(plan) : tx("ยังไม่มี", "none")}
          </div>
        </div>
      ) : null}

      <div className="u-mt-16" style={{ display: "flex", gap: 8, justifyContent: "center", flexWrap: "wrap" }}>
        <Link to="/admin/subscription">
          <Button variant="primary">{tx("อัปเกรดแพ็กเกจ", "Upgrade plan")}</Button>
        </Link>
        {/* ซื้อเฉพาะโมดูลนี้เป็นฟีเจอร์เสริมได้ ไม่ต้องอัปเกรดทั้งแพ็กเกจ (ฟีเจอร์เสริมต้องมีแพ็กเกจหลักก่อน — ร้านฟรีไม่โชว์) */}
        {!planFree && MODULE_ADDON_PRICES[moduleKey] ? (
          <Link to="/admin/addons?tab=features">
            <Button>
              {tx(`ซื้อแยกเฉพาะฟีเจอร์นี้ ${baht(MODULE_ADDON_PRICES[moduleKey], 0)} / เดือน`, `Buy this feature only ${baht(MODULE_ADDON_PRICES[moduleKey], 0)} / month`)}
            </Button>
          </Link>
        ) : null}
      </div>
    </Card>
  );
}

/** หน้าจอเมื่อแพ็กเกจครอบคลุมแล้ว แต่เจ้าของร้านปิดโมดูลไว้เอง */
function ModuleOff({ module: moduleKey }) {
  const { tx, pick } = useI18n();
  const feature = FEATURE_MODULE_MAP[moduleKey];

  return (
    <Card className="u-center" style={{ padding: 32 }}>
      <div style={{ fontSize: 40, color: "var(--c-indigo-600)" }}><Icon name="modules" /></div>
      <h2 style={{ fontSize: 20, fontWeight: 800, margin: "10px 0 4px" }}>
        {tx("โมดูลนี้ถูกปิดอยู่", "This module is turned off")}
      </h2>
      <div className="u-muted u-fs-13">
        {feature ? pick(feature) : moduleKey} — {tx("เปิดใช้งานได้ที่หน้าจัดการโมดูล", "Turn it on in the module manager")}
      </div>
      <Link to="/admin/modules" className="u-mt-16" style={{ display: "inline-block" }}>
        <Button variant="primary">{tx("ไปหน้าจัดการโมดูล", "Open module manager")}</Button>
      </Link>
    </Card>
  );
}

/** ครอบทุก route ของ /admin ที่ต้องมีแพ็กเกจ */
export default function PlanRoute({ module: moduleKey, children }) {
  const { ready, moduleLocked, hasModule } = useApp();

  // ยังโหลดข้อมูลร้านไม่เสร็จ อย่าเพิ่งเด้ง ไม่งั้นจะกระพริบไปหน้าแพ็กเกจทุกครั้งที่รีเฟรช
  if (!ready) return null;

  if (moduleKey && moduleLocked(moduleKey)) return <LockedFeature module={moduleKey} />;
  if (moduleKey && !hasModule(moduleKey)) return <ModuleOff module={moduleKey} />;

  return children;
}

/** ป้ายกุญแจเล็ก ๆ ใช้ในเมนู/การ์ดโมดูล */
export const LockChip = ({ label }) => (
  <Badge tone="gray">
    <Icon name="lock" /> {label}
  </Badge>
);
